import React from 'react';
import { Box, Button } from '@mui/material';
import {
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';

// ============================================================================
//  ExportButtons — shared component for Excel + PDF export from any section
//
//  Props:
//    filename      string  — base filename (no extension)
//    title         string  — title for PDF header
//    subtitle      string  — optional subtitle
//    sheets        array   — [{ name, headers, rows }, ...]
//    orientation   string  — 'landscape' (default) or 'portrait' for PDF
// ============================================================================

const todayStr = () => new Date().toISOString().slice(0, 10);
export { todayStr };

async function exportExcel({ filename, sheets }) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  sheets.forEach(s => {
    const data = [s.headers, ...s.rows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = s.headers.map(h => ({ wch: Math.min(Math.max(String(h).length + 2, 10), 30) }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, s.name.slice(0, 31));
  });
  XLSX.writeFile(wb, filename, { compression: true });
}

async function exportPDF({ filename, title, subtitle, sheets, orientation = 'landscape' }) {
  const { jsPDF } = await import('jspdf');
  await import('jspdf-autotable');
  const doc = new jsPDF({ orientation });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 14;

  doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 40, 40);
  doc.text(title, pageWidth / 2, y, { align: 'center' }); y += 6;
  if (subtitle) {
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100);
    doc.text(subtitle, pageWidth / 2, y, { align: 'center' }); y += 4;
  }
  doc.setFontSize(8); doc.setTextColor(150, 150, 150);
  doc.text('Generated: ' + new Date().toLocaleString(), pageWidth / 2, y, { align: 'center' }); y += 6;

  sheets.forEach((s, idx) => {
    if (idx > 0) y = doc.lastAutoTable.finalY + 8;
    if (y > 180) { doc.addPage(); y = 14; }
    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(91, 33, 182);
    doc.text(s.name, 14, y); y += 2;
    doc.autoTable({
      startY: y + 2,
      head: [s.headers], body: s.rows,
      theme: 'grid',
      headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
      styles: { cellPadding: 2 },
    });
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`CCC4CS Cold Store · Page ${i} of ${pageCount}`,
             pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
  }
  doc.save(filename);
}

export default function ExportButtons({ filename, title, subtitle, sheets, orientation = 'landscape', size = 'small' }) {
  const handleExcel = async () => {
    try { await exportExcel({ filename: filename + '.xlsx', sheets }); }
    catch (e) { console.error('Excel export failed:', e); alert('Excel export failed: ' + e.message); }
  };
  const handlePDF = async () => {
    try { await exportPDF({ filename: filename + '.pdf', title, subtitle, sheets, orientation }); }
    catch (e) { console.error('PDF export failed:', e); alert('PDF export failed: ' + e.message); }
  };

  return (
    <Box sx={{ display: 'inline-flex', gap: 1 }}>
      <Button
        size={size}
        variant="contained"
        startIcon={<ExcelIcon sx={{ fontSize: 18 }} />}
        onClick={handleExcel}
        sx={{
          background: 'linear-gradient(135deg, #16a34a, #15803d)',
          color: 'white', fontWeight: 700, fontSize: 13, textTransform: 'none',
          px: 2.5, py: 1, minHeight: 36,
          boxShadow: '0 1px 3px rgba(22,163,74,0.3)',
          '&:hover': { background: 'linear-gradient(135deg, #15803d, #14532d)' },
        }}
      >
        Excel
      </Button>
      <Button
        size={size}
        variant="contained"
        startIcon={<PdfIcon sx={{ fontSize: 18 }} />}
        onClick={handlePDF}
        sx={{
          background: 'linear-gradient(135deg, #ea580c, #c2410c)',
          color: 'white', fontWeight: 700, fontSize: 13, textTransform: 'none',
          px: 2.5, py: 1, minHeight: 36,
          boxShadow: '0 1px 3px rgba(234,88,12,0.3)',
          '&:hover': { background: 'linear-gradient(135deg, #c2410c, #9a3412)' },
        }}
      >
        PDF
      </Button>
    </Box>
  );
}

// ============================================================================
// Helper: flatten a tree (with .children) into rows for export
// Bulletproofed v2 — skips null/undefined nodes, falls back when .name missing,
// guards against non-array children. Survives malformed backend responses
// (e.g. NULL group keys from build_hierarchy()).
// ============================================================================
export function flattenTree(nodes, parents = []) {
  const out = [];
  if (!Array.isArray(nodes)) return out;
  nodes.forEach(n => {
    if (!n || typeof n !== 'object') return;          // skip null / undefined / primitives
    const name = (n.name ?? n.label ?? '(unspecified)') + '';  // coerce to string
    const path = [...parents, name];
    out.push({ level: parents.length, path, ...n, name });
    if (Array.isArray(n.children) && n.children.length > 0) {
      out.push(...flattenTree(n.children, path));
    }
  });
  return out;
}
